import { makeAutoObservable, runInAction, configure } from 'mobx';
import { createWishListAndAddItem } from '../helpers/wishList/CreateWishListAndAddItem';
import { addItemToWishList } from '../helpers/wishList/AddItemToWishList/AddItemToWishList';
import { getAndMapWishLists } from '../helpers/wishList/GetAndMapWishLists';
import {
    getSkuId,
    getSkuSelected,
    getProductMetadata,
    getProductDetails,
    getExperiences,
} from '../helpers/productDetails/productDetails';
import {
    GENERIC_ERROR_MESSAGE,
    LIST_FULL_ERROR_MESSAGE,
    SELECT_SIZE_ERROR_MESSAGE,
    WISHLIST_PDP_CLICK_EVENT_KEY,
    WISHLIST_PDP_SIZE_ERROR,
    WISH_LIST_QUERY_STRING,
    WL_PRODUCT_QUERY_STRING,
    MONETATE_ITEM_REMOVED_FROM_RECS_KEY,
    MONETATE_ITEM_REMOVED_FROM_RECS_ERROR_KEY,
} from './constants';
import {
    isItemInWishList,
    updateProductWishList,
    mapProductWishLists,
    removeQueryParamFromURL,
    logAPIErrorToNewrelic,
    mapWishLists,
} from '../helpers/wishList/utils';
import { deleteItemFromWishList } from '../helpers/wishList/DeleteItemFromWishList/DeleteItemFromWishList';
import { deleteWishListProduct } from '../helpers/wishList/DeleteWishListProduct/DeleteWishListProduct';
import EmailModalStore from './EmailModalStore/EmailModalStore';
import { APIResult } from '../helpers/wishList/APIResult';
import { FEATURE_FLAGS } from '../helpers/featureFlags/constants';
import { isFlagSet } from '../helpers/featureFlags/featureFlags';
import { postMonetateImpressionByName, shouldCollapseWishLists, trackEvent } from '../helpers/monetate/monetate';
import { log } from '../utils/logger';
import { GetVariants } from '../helpers/wishList/GetVariants/GetVariants';
import { createWishList } from '../helpers/wishList/CreateWishList/CreateWishList';
import { deleteWishListSku } from '../helpers/wishList/DeleteWishListSku/DeleteWishListSku';
import { getProductNotificationDetails } from '../helpers/quickViewProductDetails/GetProductNotificationDetails';

// Add support for browsers without Proxy object support
configure({ useProxies: 'ifavailable' });

type MODAL = 'remove' | 'variant' | 'wishlist' | 'email';

class WishListsStore {
    constructor(emailModalStore?: EmailModalStore) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.emailModalStore = emailModalStore || undefined;
    }

    mode?: string;
    // Icon mode variables
    currentModal?: MODAL;
    products: WishListIconProduct[] = [];
    selectedWishListIds: string[] = [];
    currentProduct?: WishListIconProduct;
    interactedProductId = '';
    selectedColour = '';
    selectedSize = '';
    // Existing variables
    wishLists: ProductWishList[] = [];
    currentProductSkuId = '';
    currentProductVariantId = '';
    currentProductId = '';
    currentAvailableProductSkus: number | undefined = undefined;
    isSkuIdSelected = false;
    errorMessage = '';
    hasUserInteracted = false;
    errorRetrievingWishlists = false;
    isCallingAPI = false;
    currentProductMetadata = '';
    emailModalStore: EmailModalStore | undefined = undefined;
    collapsedByDefault: boolean | undefined = undefined;
    collapsed = true;
    variantChanged = false;
    // The variant on the PDP page
    pdpSelectedSku: string | undefined = undefined;

    get wishListsLength(): number {
        return this.wishLists.length;
    }

    get hasOneOrMoreWishlist(): boolean {
        return this.wishLists.length >= 1;
    }

    get hasMoreThanOneWishlist(): boolean {
        return this.wishLists.length > 1;
    }

    get hasError(): boolean {
        return this.errorMessage.length > 0;
    }

    get productExistsInWishLists(): boolean {
        return this.wishLists.some((wishList) => {
            return wishList.isAdded;
        });
    }

    get shouldWishListsBeExpanded(): boolean {
        // we always have the slight expand if we need to show an error or if the item is in the list
        if (this.hasError || this.productExistsInWishLists) {
            return true;
        }

        // if we have opt-ed into the collapse test, and the default is collapsed then we collapse it
        if (this.collapsed) {
            return false;
        }

        // we expand if they have more than 1 list, and the item is not in the lists and is not in the collapse test
        return this.hasMoreThanOneWishlist;
    }

    // What are the sizes available for a particular colour of product (Not all colours have the same set of sizes)
    get sizesForColour(): string[] {
        return (
            this.currentProduct?.skus
                ?.filter((sku) => sku.colour === this.selectedColour)
                .map((sku) => sku.size)
                .filter((size) => size != undefined && size != '') || []
        );
    }

    setMode(mode?: string): void {
        runInAction(() => {
            this.mode = mode;
        });
    }

    setModal(modal?: MODAL): void {
        runInAction(() => {
            this.currentModal = modal;
            this.interactedProductId = '';
        });
    }

    reset(): void {
        // Don't reset state of selected sku if on PDP - we do now as we don't select colour or size in modal
        if (this.mode === 'icon-placeholder-basket') {
            runInAction(() => {
                this.selectedWishListIds = [];
                this.currentModal = undefined;
                this.errorMessage = '';
                if (this.pdpSelectedSku) {
                    // reset the state to what's currently select on PDP
                    this.setCurrentProductSkuId(this.pdpSelectedSku);
                    this.variantChanged = false;
                }
            });
        } else {
            this.setCurrentProduct();

            runInAction(() => {
                this.currentModal = undefined;
                this.selectedWishListIds = [];
                this.selectedColour = '';
                this.selectedSize = '';
                this.errorMessage = '';
                this.interactedProductId = '';
            });
        }
    }

    resetProductsList(): void {
        runInAction(() => {
            this.products = [];
        });
    }

    addIconProduct(product: WishListIconProduct): void {
        runInAction(() => {
            this.products = [...this.products, product];
        });
    }

    setCurrentProduct(product?: WishListIconProduct): void {
        runInAction(() => {
            this.currentProduct = product;
            this.currentProductId = product?.id || '';
            this.currentProductSkuId = product?.skuId || '';
            this.currentProductVariantId = product?.variantId || '';
        });
    }

    setCurrentProductId(productId: string): void {
        runInAction(() => {
            this.currentProductId = productId;
        });
    }

    setCurrentProductSkuId(productSkuId: string): void {
        runInAction(() => {
            this.currentProductSkuId = productSkuId;
        });
    }

    setCurrentProductVariantId(productVariantId: string): void {
        runInAction(() => {
            this.currentProductVariantId = productVariantId;
        });
    }

    setSelectedColour(colour: string): void {
        runInAction(() => {
            this.selectedColour = colour;
        });
    }

    setSelectedSize(size: string): void {
        runInAction(() => {
            this.selectedSize = size;
        });
    }

    isProductInWishList = (productId: string, productSkuId?: string, wishlistId?: string): boolean => {
        if (wishlistId) {
            return (
                this.wishLists
                    .find(({ id }) => id === wishlistId)
                    ?.products.some((p) => p.productId === productId || p.skuId === productSkuId) || false
            );
        }

        return this.wishLists.some(({ products }) =>
            products.some((p) => p.productId === productId || p.skuId === productSkuId),
        );
    };

    isSkuInWishList = (productSkuId?: string, wishlistId?: string): boolean => {
        if (wishlistId) {
            return (
                this.wishLists.find(({ id }) => id === wishlistId)?.products.some((p) => p.skuId === productSkuId) ||
                false
            );
        }
        return this.wishLists.some(({ products }) => products.some((p) => p.skuId === productSkuId));
    };

    async handleEmailMeModal(): Promise<void> {
        const productDetails = this.parseProductDetails(getProductDetails());
        // If the product is not out of stock, we do not need to show the email me modal
        if (!productDetails?.productId === undefined || !productDetails?.isOutOfStock) return;

        const productId = productDetails?.productId as string;
        const productNotificationDetail = await this.handledAPIResult(() =>
            getProductNotificationDetails(productId, this.currentProductSkuId),
        );

        const customerNotifiableEvents =
            productNotificationDetail?.customerNotifiableEvents as CustomerNotifiableEvents;

        if (!productNotificationDetail?.emailMeWhenBackInStock || !customerNotifiableEvents) return;

        this.emailModalStore?.openEmailModal(
            {
                skuId: this.currentProductSkuId,
                productId: productId,
            },
            customerNotifiableEvents,
        );
    }

    checkSkuSelected(): void {
        this.isSkuIdSelected = getSkuSelected();
        if (this.hasUserInteracted && !this.isSkuIdSelected) {
            const experiences = getExperiences();

            postMonetateImpressionByName('HM | AB-6138 | Wish list error handling on PDP', experiences);

            const isInErrorExperience = isFlagSet('pdp_error_handling', experiences);

            if (isInErrorExperience) {
                const customEvent = new CustomEvent('ADD_TO_WISHLIST_ESI:WARNING_OR_ERROR', {
                    detail: {
                        code: 'no-size-selected',
                    },
                });
                window.dispatchEvent(customEvent);
            } else {
                this.errorMessage = SELECT_SIZE_ERROR_MESSAGE;
            }
            trackEvent(WISHLIST_PDP_SIZE_ERROR);
        }
    }

    checkIfWishListInURL(): void {
        if (this.mode !== 'button') return;

        const queryParams = window.location.search;
        // this needs the undefined check for the tests which do not return search
        if (queryParams && queryParams.includes(WISH_LIST_QUERY_STRING)) {
            removeQueryParamFromURL(window.location.href, WISH_LIST_QUERY_STRING);
            this.hasUserInteracted = true;
            !this.hasMoreThanOneWishlist && this.handleWishListOnClick('');
        }
    }

    checkIfProductInURL(): void {
        if (this.mode === 'button') return;

        const queryParams = new URLSearchParams(window.location.search);
        const interactedProductId = queryParams.get(WL_PRODUCT_QUERY_STRING);
        if (interactedProductId) {
            removeQueryParamFromURL(window.location.href, `${WL_PRODUCT_QUERY_STRING}=${interactedProductId}`);
            this.interactedProductId = interactedProductId;
        }
    }

    setupEventListeners(): void {
        if (this.mode === 'button' || this.mode === 'icon-placeholder-pdp') {
            document.body.addEventListener('displayedProduct', ((event: CustomEvent) => {
                runInAction(() => {
                    this.errorMessage = '';
                    this.currentProductSkuId = event.detail.skuId;
                    if (event.detail.productMetadata) {
                        this.currentProductMetadata = JSON.stringify(event.detail.productMetadata);
                    }
                });
            }) as EventListener);
        }
        if (this.mode === 'icon-placeholder-basket') {
            // Changing the Size or Colour on the PDP page will generate this event. The event will contain the sku
            // that's applicable to the current set of selections (Colour and Size)
            document.body.addEventListener('displayedProduct', ((event: CustomEvent) => {
                runInAction(() => {
                    this.currentProductSkuId = event.detail.skuId;
                    this.variantChanged = false;
                });
            }) as EventListener);
        }
    }

    mapProductWishListForCurrentSku(): void {
        if (this.mode !== 'button') return;

        if (this.wishLists) {
            const productMetadata = this.parseCurrentProductMetadata();
            runInAction(
                () => (this.wishLists = mapProductWishLists(this.wishLists, this.currentProductSkuId, productMetadata)),
            );
        }
    }

    countWishListsContainingSku(): number {
        return this.wishLists.reduce((count, wishList) => {
            return wishList.products.filter((product) => product.skuId === this.currentProductSkuId).length > 0
                ? count + 1
                : count;
        }, 0);
    }

    handleWishListOnClick = async (wishListId: string): Promise<void> => {
        if (this.mode === 'button') {
            trackEvent(WISHLIST_PDP_CLICK_EVENT_KEY);
        }

        if (!wishListId && this.collapsedByDefault && this.collapsed) {
            runInAction(() => {
                this.collapsed = false;
            });
        }

        if (!wishListId && this.hasMoreThanOneWishlist) {
            // do nothing when multiple lists exist and a user clicks the top button
            return;
        }

        !this.hasUserInteracted &&
            runInAction(() => {
                this.hasUserInteracted = true;
            });

        const { currentProductSkuId, errorRetrievingWishlists } = this;

        if (errorRetrievingWishlists) {
            await this.getWishlists(false);
        }

        this.checkSkuSelected();
        if (!this.isSkuIdSelected) {
            const experiences = getExperiences();

            const isInErrorExperience = isFlagSet('pdp_error_handling', experiences);

            if (!isInErrorExperience) {
                runInAction(() => {
                    this.errorMessage = SELECT_SIZE_ERROR_MESSAGE;
                });
            }
            return;
        }

        runInAction(() => {
            // no pre-conditions stopping the add, reset and try adding product
            this.errorMessage = '';
        });

        if (!this.wishLists || this.wishListsLength == 0) {
            const productMetadata = this.parseCurrentProductMetadata();
            const productDetail = this.parseProductDetails(getProductDetails());
            const itemDetails = this.constructItemDetails(currentProductSkuId);
            const mode = this.mode;

            const itemPrice = window.jlData?.page?.productPages?.price;

            const isAddingFromOOSItem =
                mode === 'icon-placeholder-pdp'
                    ? this.currentProduct?.useOutOfStockButton
                    : productDetail?.isOutOfStock && mode === 'button';

            const newWishLists = await this.handledAPIResult(() =>
                createWishListAndAddItem(
                    currentProductSkuId,
                    itemDetails,
                    productMetadata,
                    mode,
                    itemPrice,
                    productDetail?.productId,
                    isAddingFromOOSItem,
                ),
            );

            if (newWishLists) {
                runInAction(() => (this.wishLists = newWishLists));
            }

            return;
        }

        if (!wishListId) {
            wishListId = this.wishLists[0].id;
        }

        const productExistsInWishlist = isItemInWishList(this.wishLists, wishListId);
        if (productExistsInWishlist) {
            await this.removeItemFromWishList(wishListId, currentProductSkuId);
        } else {
            await this.addToWishList(currentProductSkuId, wishListId);
        }
    };

    /**
     * Updates the URL on the PDP page to the URL of the currently selected SKU (as if it had been selected on the
     * PDP itself
     */
    updateURL(): void {
        const currentSkuData = this.currentProduct?.skus?.find((sku) => sku.id === this.currentProductSkuId);
        if (currentSkuData) {
            // event to update the PDP page to reflect the chosen variant
            const customEvent = new CustomEvent('dataStateChange', {
                detail: {
                    action: 'global.interaction',
                    type: 'pdp - wishlist',
                    subType: 'modal - choose variant',
                    currentSkuUrl: currentSkuData.url,
                    currentSku: currentSkuData.id,
                },
            });
            this.dispatchCustomEvent(customEvent);
        }
    }

    handleWishListIconClick = async (product: WishListIconProduct, remove = false): Promise<void> => {
        runInAction(() => {
            this.interactedProductId = '';
        });

        if (this.mode === 'icon-placeholder-pdp') {
            trackEvent(WISHLIST_PDP_CLICK_EVENT_KEY);
        }

        if (this.mode === 'icon-placeholder-pdp' || this.mode === 'icon-placeholder-basket') {
            const currentlySelectedSku = this.currentProductSkuId;
            const currentlySelectedVariant = this.currentProductVariantId;
            this.setCurrentProduct(product);
            // Set it back to the sku selected by the event
            if (currentlySelectedSku) {
                this.setCurrentProductSkuId(currentlySelectedSku);
            }
            if (currentlySelectedVariant) {
                this.setCurrentProductVariantId(currentlySelectedVariant);
            }
        } else {
            this.setCurrentProduct(product);
        }

        if (remove) {
            if (
                (product.isMultiSku && this.mode === 'icon-placeholder') ||
                (this.countWishListsContainingSku() > 1 &&
                    (this.mode === 'icon-placeholder-pdp' ||
                        this.mode === 'icon-placeholder-basket' ||
                        this.mode === 'icon-placeholder-wishlist-recs'))
            ) {
                this.setModal('remove');
            } else {
                if (this.mode === 'icon-placeholder' || this.mode === 'icon-placeholder-wishlist-recs') {
                    await this.removeProductFromWishLists(product.id, this.mode);
                } else {
                    // icon-placeholder-pdp or icon-placeholder-basket or icon-placeholder-pdp-button
                    await this.removeSkuFromWishLists(product.skuId);
                }
            }
            return;
        }

        if (product.isMultiSku) {
            const data = await this.handledAPIResult(() => GetVariants(product.id));
            const hasSizeAndColorVariants = (data?.sizes?.length ?? 0) > 0 || (data?.colours?.length ?? 0) > 0;
            if (hasSizeAndColorVariants) {
                if (this.mode === 'icon-placeholder-pdp') {
                    const currentProductVariantId = this.currentProductVariantId;
                    const currentProductSkuId = this.currentProductSkuId;
                    this.setCurrentProduct({ ...product, ...data });
                    this.setCurrentProductSkuId(currentProductSkuId);
                    this.setCurrentProductVariantId(currentProductVariantId);
                    if (!this.currentProductVariantId) {
                        // event to update the PDP page to show size error
                        const customEvent = new CustomEvent('ADD_TO_WISHLIST_ESI:WARNING_OR_ERROR', {
                            detail: {
                                code: 'no-size-selected',
                            },
                        });
                        trackEvent(WISHLIST_PDP_SIZE_ERROR);
                        window.dispatchEvent(customEvent);
                        return;
                    }
                } else {
                    this.setModal('variant');
                    this.dispatchGlobalEvent(
                        'global.interaction',
                        this.getActionType() + ' - wishlist',
                        'modal - choose variant',
                        product,
                        undefined,
                    );
                }

                if (!data) {
                    runInAction(() => {
                        this.errorMessage = 'error';
                    });
                } else {
                    // PDP Flow - with selected Product set by event
                    if (
                        (this.mode === 'icon-placeholder-pdp' || this.mode === 'icon-placeholder-basket') &&
                        this.currentProduct
                    ) {
                        const currentProductSkuId = this.currentProductSkuId;
                        this.setCurrentProduct({ ...product, ...data });
                        this.setCurrentProductSkuId(currentProductSkuId);
                        const skuVariant = this.currentProduct?.skus?.find(
                            (sku) => sku.id === this.currentProductSkuId,
                        );
                        this.setSelectedColour(skuVariant?.colour);
                        this.setSelectedSize(skuVariant?.size);
                    } else {
                        this.setCurrentProduct({ ...product, ...data });
                        if (data.colours && data.colours.length) {
                            this.setSelectedColour(data.colours[0].colour);
                            // The currentProductSkuId might be defaulted to a different sku e.g. out of stock variant
                            const variant = this.currentProduct?.skus?.find(
                                (sku) => sku.colour === data.colours[0].colour,
                            );
                            if (variant) {
                                this.setCurrentProductSkuId(variant.id);
                            }
                        }
                    }
                }
                if (this.mode !== 'icon-placeholder-pdp') {
                    return;
                }
            }
        }

        await this.waitForGetWishListAPICompletion();

        if (this.hasMoreThanOneWishlist) {
            this.setModal('wishlist');
            this.dispatchGlobalEvent(
                'global.interaction',
                this.getActionType() + ' - wishlist',
                'modal - select a list',
                product,
                undefined,
            );
            return;
        } else {
            this.dispatchGlobalEvent('add.wishlist', this.getActionType(), 'add to wishlist', product, undefined);
        }

        await this.addToWishList(product.skuId, '');

        if (this.currentProduct?.useOutOfStockButton && this.mode === 'icon-placeholder-pdp') {
            this.setModal('email');
            return;
        }
    };

    dispatchCustomEvent(customEvent: CustomEvent): void {
        document.body.dispatchEvent(customEvent);
    }

    getActionType(): string {
        if (this.mode === 'icon-placeholder-pdp') {
            return 'pdp';
        } else if (this.mode === 'icon-placeholder-wishlist-recs') {
            return 'recs';
        } else if (this.mode === 'icon-placeholder-basket') {
            return 'basket';
        } else if (this.mode === 'icon-placeholder') {
            return 'plp';
        } else {
            return 'pdp';
        }
    }

    extractItemPriceFromJLData(type: string, skuId: string | undefined): number | undefined {
        let itemPrice = undefined;
        if (this.mode === 'icon-placeholder-basket') {
            itemPrice = window.jlData?.basket?.product?.find((product) => product?.sku == skuId)?.itemPrice;
        } else {
            if (this.mode === 'icon-placeholder-pdp') {
                const price = window.jlData?.page?.productPages?.price;
                itemPrice = price ? parseFloat(price) : undefined;
            } else {
                if (this.mode === 'icon-placeholder') {
                    const price = window.jlData?.page?.productPages?.products?.chunkDetails?.products?.find(
                        (product) => product.sku == skuId,
                    )?.priceNow;
                    itemPrice = price ? parseFloat(price) : undefined;
                }
            }
        }
        return itemPrice;
    }

    extractItemPriceFromSelectedSku(selectedProduct: WishListIconProduct): number | undefined {
        const price = selectedProduct.skus?.find((sku) => sku.id === selectedProduct.skuId)?.prices?.now;
        return price ? parseFloat(price) : undefined;
    }

    dispatchGlobalEvent(
        action: string,
        type: string,
        subType: string,
        product: WishListIconProduct | undefined,
        modalStep: string | undefined,
    ): void {
        let additionalInfo = {};
        if (modalStep) {
            additionalInfo = { modalstep: modalStep };
        }
        let itemPrice = undefined;
        if (product && product.isMultiSku) {
            itemPrice = this.extractItemPriceFromSelectedSku(product);
        } else {
            itemPrice = this.extractItemPriceFromJLData(type, product?.skuId);
        }
        const customEvent = new CustomEvent('dataStateChange', {
            detail: {
                action: action,
                type: type,
                subType: subType,
                additionalInfo: additionalInfo,
                products: {
                    productId: product?.id ? product?.id : undefined,
                    itemPrice: itemPrice,
                    qty: '1',
                    sku: product?.skuId,
                },
            },
        });
        setTimeout(this.dispatchCustomEvent, 500, customEvent);
    }

    handleWishListChange(wishListId: string, selected: boolean): void {
        const wishListIds = [...this.selectedWishListIds];
        const wishListIndex = wishListIds.indexOf(wishListId);

        if (selected) {
            if (wishListIndex === -1) {
                wishListIds.push(wishListId);
            }
        } else {
            if (wishListIndex !== -1) {
                wishListIds.splice(wishListIndex, 1);
            }
        }

        runInAction(() => {
            this.selectedWishListIds = wishListIds;
        });
    }

    handledAPIResult = async <T>(getData: () => Promise<APIResult<T>>): Promise<T | null> => {
        let showError = false;
        let errorMessage = GENERIC_ERROR_MESSAGE;

        try {
            runInAction(() => {
                this.isCallingAPI = true;
            });

            const apiResult = await getData();
            if (apiResult.success) {
                if (apiResult.data == null) {
                    logAPIErrorToNewrelic(`handledAPIResult`, `getData | Unexpected data scenario | No Data`, {
                        ...apiResult,
                        response: apiResult,
                        errorThrown: true,
                    });
                    throw Error('Unexpected data scenario');
                }

                return apiResult.data;
            } else {
                if (apiResult.showError) {
                    showError = true;
                    if (apiResult.errorMessage) {
                        errorMessage = apiResult.errorMessage;
                    }
                    logAPIErrorToNewrelic(`handledAPIResult`, `getData | Response not OK`, {
                        ...apiResult,
                        response: apiResult,
                    });
                }
            }
        } catch (error) {
            showError = true;
            logAPIErrorToNewrelic(`handledAPIResult`, `getData | Exception`, {
                error: error,
                showError: showError,
            });
        } finally {
            runInAction(() => {
                this.isCallingAPI = false;
            });
        }

        if (showError) {
            runInAction(() => (this.errorMessage = errorMessage));
        }

        return null;
    };

    // API Calls
    getWishlists = async (allowedToFail = true): Promise<void> => {
        if (this.mode === 'button') {
            runInAction(() => {
                this.currentProductSkuId = this.currentProductSkuId || getSkuId();
                this.currentProductMetadata = this.currentProductMetadata || getProductMetadata();
            });
        }

        const wishLists = await this.handledAPIResult(() => getAndMapWishLists(allowedToFail));
        if (wishLists) {
            runInAction(() => {
                this.wishLists = wishLists;
                this.errorRetrievingWishlists = false;
            });

            this.setCollapsedByDefault();
        } else {
            this.errorRetrievingWishlists = true;
            logAPIErrorToNewrelic(`getWishlists`, `getAndMapWishLists | errorRetrievingWishlists`, {
                response: wishLists,
            });
        }
    };

    createWishList = async (): Promise<string> => {
        const wishlist = await this.handledAPIResult(() => createWishList());
        if (wishlist) {
            runInAction(
                () =>
                    (this.wishLists = [
                        {
                            id: wishlist.id,
                            name: wishlist.name,
                            products: [],
                            isAdded: false,
                            isFull: false,
                            shared: false,
                        },
                    ]),
            );
        }

        return wishlist?.id ?? '';
    };

    private checkProductInWishList(productSkuId: string, wishListId: string): boolean {
        if (this.mode === 'button') {
            return this.wishListsLength === 1 && this.productExistsInWishLists;
        }

        if (this.mode === 'icon-placeholder' || this.mode === 'icon-placeholder-wishlist-recs') {
            return this.isProductInWishList('', productSkuId, wishListId);
        }

        if (this.mode === 'icon-placeholder-pdp' || this.mode === 'icon-placeholder-basket') {
            return this.isSkuInWishList(productSkuId, wishListId);
        }

        return false;
    }

    addToWishList = async (productSkuId: string, wishListId?: string, isMultipleWishlists?: boolean): Promise<void> => {
        if (!this.wishLists || this.wishListsLength == 0) {
            // Create new default wishlist if no wishlists exist
            wishListId = await this.createWishList();
        }

        const wishList = wishListId ? this.wishLists.find(({ id }) => id === wishListId) : this.wishLists[0];
        if (!wishList) {
            logAPIErrorToNewrelic(`addToWishList`, `Wishlist not found!`, {
                wishListIdToUse: wishListId,
                currentProductSkuId: productSkuId,
            });
            return;
        }

        const productMetadata = this.parseCurrentProductMetadata();
        const productDetail = this.parseProductDetails(getProductDetails());

        const productExistsInWishLists = this.checkProductInWishList(productSkuId, wishList.id);

        if (productExistsInWishLists) {
            logAPIErrorToNewrelic(`addToWishList`, `Product already in List`, {
                wishListIdToUse: wishList.id,
                currentProductSkuId: productSkuId,
                productMetadata: productMetadata,
            });
            return;
        }

        if (wishList.isFull) {
            runInAction(() => {
                this.errorMessage = LIST_FULL_ERROR_MESSAGE;
            });

            logAPIErrorToNewrelic(`addToWishList`, `List Full`, {
                wishListIdToUse: wishList.id,
                currentProductSkuId: productSkuId,
                productMetadata: productMetadata,
                errorMessage: this.errorMessage,
            });
            return;
        }

        const itemDetails = this.constructItemDetails(productSkuId);
        const mode = this.mode;
        const itemPrice = window.jlData?.page?.productPages?.price;

        const isAddingFromOOSItem =
            mode === 'icon-placeholder-pdp'
                ? this.currentProduct?.useOutOfStockButton
                : productDetail?.isOutOfStock && mode === 'button';

        const updatedWishList = await this.handledAPIResult(() =>
            addItemToWishList(
                wishList.id,
                productSkuId,
                itemDetails,
                productMetadata,
                mode,
                itemPrice,
                productDetail?.productId,
                isMultipleWishlists,
                isAddingFromOOSItem,
            ),
        );

        if (updatedWishList) {
            runInAction(() => {
                this.wishLists = updateProductWishList(this.wishLists, {
                    ...updatedWishList,
                    isAdded: true,
                    isFull: false,
                });

                this.handleEmailMeModal();
            });
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructItemDetails(currentSkuId: string): Record<string, any> {
        return {
            currentSkuId: currentSkuId,
            currentProductId: this.currentProductId,
            currentAvailableProductSkus: this.currentAvailableProductSkus,
            isSkuIdSelected: this.isSkuIdSelected,
            errorMessage: this.errorMessage,
            hasUserInteracted: this.hasUserInteracted,
            errorRetrievingWishlists: this.errorRetrievingWishlists,
            currentProductMetadata: this.currentProductMetadata,
        };
    }

    removeItemFromWishList = async (wishListId: string, skuId: string): Promise<void> => {
        const productMetadata = this.parseCurrentProductMetadata();

        const updatedWishList = await this.handledAPIResult(() =>
            deleteItemFromWishList(wishListId, skuId, productMetadata),
        );

        if (updatedWishList) {
            const productWishList: ProductWishList = { ...updatedWishList, isAdded: false, isFull: false };
            const newUpdatedWishLists = updateProductWishList(this.wishLists, productWishList);
            runInAction(() => {
                this.wishLists = newUpdatedWishLists;
            });
        }
    };

    parseProductDetails = (json: string): ProductDetails | undefined => {
        try {
            if (json) {
                return JSON.parse(json) as ProductDetails;
            }
        } catch {
            // invalid product metadata
            logAPIErrorToNewrelic(`parseProductDetails`, `Invalid product metadata`, {
                json: json,
            });
        }

        return undefined;
    };

    parseCurrentProductMetadata = (): ProductMetadata | undefined => {
        try {
            if (this.currentProductMetadata) {
                return JSON.parse(this.currentProductMetadata) as ProductMetadata;
            }
        } catch {
            // invalid product metadata
            logAPIErrorToNewrelic(`parseCurrentProductMetadata`, `Invalid product metadata`, {
                currentProductMetadata: this.currentProductMetadata,
            });
        }

        return undefined;
    };

    setCollapsedByDefault = async (): Promise<void> => {
        if (this.wishListsLength > 1 && this.collapsedByDefault === undefined) {
            const shouldCollapse = await shouldCollapseWishLists();
            log('Collapsing wish lists:', shouldCollapse);

            runInAction(() => {
                this.collapsedByDefault = shouldCollapse || isFlagSet(FEATURE_FLAGS.COLLAPSED_BUTTON);
                this.collapsed = this.collapsedByDefault;
            });
        }
    };

    removeProductFromWishLists = async (productId: string, mode: string): Promise<void> => {
        const wishLists = await this.handledAPIResult(() => deleteWishListProduct(productId));
        const isRemoveFromRecs = mode == 'icon-placeholder-wishlist-recs';

        if (!wishLists) {
            logAPIErrorToNewrelic(`removeProductFromWishLists`, `Failed to remove product`, {
                currentProductId: productId,
                response: wishLists,
            });
            isRemoveFromRecs && trackEvent(MONETATE_ITEM_REMOVED_FROM_RECS_ERROR_KEY);
            return;
        }

        runInAction(() => {
            isRemoveFromRecs && trackEvent(MONETATE_ITEM_REMOVED_FROM_RECS_KEY);
            this.wishLists = mapWishLists(wishLists);
        });
    };

    removeSkuFromWishLists = async (skuId: string): Promise<void> => {
        const wishLists = await this.handledAPIResult(() => deleteWishListSku(skuId));

        if (!wishLists) {
            logAPIErrorToNewrelic(`removeProductFromWishLists`, `Failed to remove product`, {
                currentSkuId: skuId,
                response: wishLists,
            });
            return;
        }

        runInAction(() => {
            this.wishLists = mapWishLists(wishLists);
        });
    };

    waitForGetWishListAPICompletion = async (maxRetries = 10, interval = 200): Promise<void> => {
        for (let retry = 0; retry < maxRetries && this.isCallingAPI; retry++) {
            await new Promise((resolve) => setTimeout(resolve, interval));
        }
    };
}

export default WishListsStore;
